
import React, { useContext } from "react";
import {
  Chip,
  Button,
  Navbar,
  NavbarBrand,
  NavbarContent,
  DropdownItem,
  Dropdown,
  DropdownMenu,
  DropdownTrigger,
} from "@heroui/react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faIdCard,
  faLaptopFile,
  faPowerOff,
  faUserTag,
} from "@fortawesome/free-solid-svg-icons";

import { useQueryClient } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";

import { LayoutContext } from "../contexts/LayoutContext.jsx";
import { setLogout } from "../state/authSlice.jsx";
import { ENUM_USER_TYPE, linksUrl } from "../constants/enum.js";
import { resetCompCode } from "../state/companySlice.jsx";

export default function Nav() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { setShowSideNav } = useContext(LayoutContext);

  const { user } = useSelector((state) => state.auth);
  const { compName, salePerData } = useSelector(
    (state) => state.company
  );

  const queryClient = useQueryClient();

  // Change this property if your Redux user object uses another role field.
  const userRole = String(user?.userType ?? "").trim();

  const isDistributor = userRole === ENUM_USER_TYPE.DISTRIBUTOR;

  // Distributor = navy/teal; Sales Person = red.
 const theme = isDistributor
  ? {
      navbar: "bg-violet-800",
      icon: "bg-violet-950 text-white",
      label: "bg-violet-900 text-violet-100",
      dropdown: "bg-white text-slate-800",
      logout:
        "text-violet-800 bg-violet-50 data-[hover=true]:bg-violet-100",
      hover:
        "data-[hover=true]:bg-violet-50 data-[hover=true]:text-violet-800",
      accent: "text-violet-700",
    }
  : {
      navbar: "bg-red-700",
      icon: "bg-red-950 text-white",
      label: "bg-red-800 text-white",
      dropdown: "bg-white text-slate-800",
      logout:
        "text-red-700 bg-red-50 data-[hover=true]:bg-red-100",
      hover:
        "data-[hover=true]:bg-red-50 data-[hover=true]:text-red-700",
      accent: "text-red-700",
    };

  const handleLogout = () => {
    dispatch(setLogout());
    dispatch(resetCompCode());
    queryClient.removeQueries();
    navigate(linksUrl.login);
  };

  const handleResetPassword = () => {
    navigate(linksUrl.resetPassword);
  };

  const funGetSubString = (str) => {
    let len = isMobile ? 15 : 19;

    if (!str) return "";

    return str.length > len ? str.substring(0, len) + ".." : str;
  };

  const companyLabel =
    compName?.length === 1
      ? funGetSubString(compName[0])
      : compName?.length > 1
        ? funGetSubString(compName[0]) + " & Other"
        : user?.clientName;

  return (
    <Navbar
      maxWidth="2xl"
      height="3rem"
      className={`z-[99] sticky top-0 ${theme.navbar} transition-colors duration-300`}
    >
      <NavbarContent>
        {/* Left side: menu, application name, company */}
        <NavbarBrand
          className={`${
            isMobile ? "text-xs" : "text-md"
          }`}
        >
          {isMobile ? (
            <>
              <Button
                variant="light"
                isIconOnly
                aria-label="Toggle navigation"
                size="sm"
                className="m-0 text-white"
                onPress={() =>
                  setShowSideNav((prev) => !prev)
                }
              >
                <FontAwesomeIcon icon={faBars} size="xl" />
              </Button>

              <Link to={linksUrl.companies}>
                <Chip
                  radius="sm"
                  variant="light"
                  className="text-xs font-semibold uppercase text-white"
                >
                  {companyLabel}
                </Chip>
              </Link>
            </>
          ) : (
            <>
              <Link to={linksUrl.dashboard}>
                <p className="m-0 whitespace-nowrap p-0 font-bold tracking-widest text-white">
                  <FontAwesomeIcon
                    className="pr-3 text-2xl"
                    icon={faLaptopFile}
                  />
                  RETAILPOS
                </p>
              </Link>

              <Button
                variant="light"
                isIconOnly
                aria-label="Toggle navigation"
                size="sm"
                className="ml-5 text-white"
                onPress={() =>
                  setShowSideNav((prev) => !prev)
                }
              >
                <FontAwesomeIcon icon={faBars} size="xl" />
              </Button>

              <Link to={linksUrl.companies}>
                <Chip
                  radius="sm"
                  variant="light"
                  className="font-semibold uppercase text-white"
                >
                  {companyLabel}
                </Chip>
              </Link>
            </>
          )}
        </NavbarBrand>

        {/* Right side: role label and user menu */}
        <NavbarBrand className="justify-end gap-1">
          <Chip
            radius="sm"
            variant="flat"
            className={`text-xs font-semibold uppercase ${theme.label}`}
          >
            {isDistributor
              ? "Purchase Order Panel"
              : salePerData?.length > 0
                ? funGetSubString(
                    salePerData[0].SalePer_Name
                  )
                : "Sales Order Panel"}
          </Chip>

          <Dropdown
            placement="bottom-end"
            radius="md"
            className="rounded-md shadow-xl"
          >
            <DropdownTrigger>
              <button
                type="button"
                aria-label="Open user menu"
                className={`cursor-pointer rounded-xl p-2 transition-colors duration-200 ${theme.icon}`}
              >
                <FontAwesomeIcon
                  icon={faUserTag}
                  size="xl"
                />
              </button>
            </DropdownTrigger>

            <DropdownMenu
              aria-label="Profile Actions"
              variant="light"
              color="default"
              size="sm"
              className={theme.dropdown}
            >
              <DropdownItem
                key="profile"
                isReadOnly
                className={`font-semibold ${theme.hover}`}
              >
                <FontAwesomeIcon
                  icon={faIdCard}
                  className={`mr-2 ${theme.accent}`}
                />
                {user?.username || "User"}
              </DropdownItem>

              <DropdownItem
                key="fullname"
                isReadOnly
                className={theme.hover}
              >
                {`${user?.firstName || ""} ${
                  user?.lastName || ""
                }`.trim()}
              </DropdownItem>

              <DropdownItem
                key="ResetPassword"
                onPress={handleResetPassword}
                className={theme.hover}
              >
                Reset Password
              </DropdownItem>

              <DropdownItem
                key="logout"
                onPress={handleLogout}
                className={`text-sm font-bold ${theme.logout}`}
              >
                <FontAwesomeIcon
                  className="mr-3"
                  icon={faPowerOff}
                />
                Logout
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </NavbarBrand>
      </NavbarContent>
    </Navbar>
  );
}